import { RegionFeatureStatus } from './region-feature-status.model';
import { RegionFeatureOverrideAbility } from './region-feature-override-ability.model';
import { DriverAssignmentMode } from './driver-assignment-mode.model';
import { ProofOfDeliveryRule } from './pod-rule.model';
import { PhotoPodRule } from './photo-pod.rule';
import { InheritableOnOffFeature, OnOffFeature } from './on-off-feature.model';
import { OrderType } from './order-type.model';
import { AutoGroupCollections } from './auto-group-collections.model';
import { SapDocumentTypeUploadToBy } from './sap-document-type-upload-to-by.model';
import { BluechatNoteFooterField } from './bluechat/bluchat-footer-note-fields.model';

class RegionFeatures {

  public id: string;
  public loadTracking                                    = RegionFeatureStatus.AVAILABLE;
  public hotspotTracking                                 = RegionFeatureStatus.AVAILABLE;
  public allowLoadsSearchFiltering                       = RegionFeatureStatus.AVAILABLE;
  public trailerYardManagement                           = RegionFeatureStatus.AVAILABLE;
  public multiCollections                                = RegionFeatureStatus.AVAILABLE;
  public multiDeliveries                                 = RegionFeatureStatus.AVAILABLE;
  public tipAndReload                                    = RegionFeatureStatus.AVAILABLE;
  public tmsLoadsSynchronization                         = RegionFeatureStatus.AVAILABLE;
  public cabinLicensePlateRequired                       = RegionFeatureStatus.AVAILABLE;
  public sapSignaturesSending                            = RegionFeatureStatus.AVAILABLE;
  public assetManagement                                 = RegionFeatureStatus.AVAILABLE;
  public quantitiesUpdateTimer                           = RegionFeatureStatus.AVAILABLE;
  public driverAssignmentModeValue                       = DriverAssignmentMode.CMAWEB_AND_CMA;
  public driverAssignmentModeOverrideAbility             = RegionFeatureOverrideAbility.OVERRIDABLE;
  public proofOfDeliveryRuleValue                        = ProofOfDeliveryRule.BOTH;
  public proofOfDeliveryRuleOverrideAbility              = RegionFeatureOverrideAbility.OVERRIDABLE;
  public photoPodRuleValue                               = PhotoPodRule.ONLY_AT_THE_DROP_LOCATION;
  public photoPodRuleOverrideAbility                     = RegionFeatureOverrideAbility.OVERRIDABLE;
  public dropAndHookValue                                = false;
  public dropAndHookOverrideAbility                      = RegionFeatureOverrideAbility.OVERRIDABLE;
  public automaticTripCreationValue                      = false;
  public automaticTripCreationOverrideAbility            = RegionFeatureOverrideAbility.OVERRIDABLE;
  public automaticTripCreationOrderTypes: OrderType[]    = [];
  public sapMultiCollectionsCheckinValue                 = InheritableOnOffFeature.OFF;
  public tripCreatedWebhookEnrichedDataOverrideAbility   = RegionFeatureOverrideAbility.OVERRIDABLE;
  public tripCreatedWebhookEnrichedDataValue             = InheritableOnOffFeature.OFF;
  public sapMultiCollectionsOverrideAbility              = RegionFeatureOverrideAbility.OVERRIDABLE;
  public autoGroupCollectionsValue                       = AutoGroupCollections.YES;
  public autoGroupCollectionsOverrideAbility             = RegionFeatureOverrideAbility.OVERRIDABLE;
  public multiAccountsValue                              = false;
  public multiAccountsOverrideAbility                    = RegionFeatureOverrideAbility.OVERRIDABLE;
  public overdueNotificationValue                        = false;
  public overdueNotificationOverrideAbility              = RegionFeatureOverrideAbility.OVERRIDABLE;
  public handoverManagementValue                         = true;
  public handoverManagementOverrideAbility               = RegionFeatureOverrideAbility.OVERRIDABLE;
  public hideVehicleIdOnDriverInformationValue           = false;
  public hideVehicleIdOnDriverInformationOverrideAbility = RegionFeatureOverrideAbility.OVERRIDABLE;
  public overdueMaxDaysUpdate                            = 14;
  public podValidationManufacturerSyncValue              = InheritableOnOffFeature.OFF;
  public podValidationManufacturerSyncOverrideAbility    = RegionFeatureOverrideAbility.OVERRIDABLE;
  public podValidationMrSyncValue                        = InheritableOnOffFeature.OFF;
  public podValidationMrSyncOverrideAbility              = RegionFeatureOverrideAbility.OVERRIDABLE;
  public yardCheckPalletsManagement                      = false;
  public yardCheckTrailersManagement                     = false;
  public reverseCheckinNonYMS                            = false;
  public reverseCheckinCustomer                          = false;
  public reverseCheckinContactEmails: string[]           = [];
  public reverseCheckinReasons: string[]                 = [];
  public sortLoadsByTripIdValue                          = InheritableOnOffFeature.OFF;
  public sortLoadsByTripIdOverrideAbility                = RegionFeatureOverrideAbility.OVERRIDABLE;
  public sortStopsByBlueyonderOrderValue                 = InheritableOnOffFeature.OFF;
  public sortStopsByBlueyonderOrderOverrideAbility       = RegionFeatureOverrideAbility.OVERRIDABLE;
  public sapDocumentTypeUploadToBlueyonder               = SapDocumentTypeUploadToBy.PROOF_OF_DELIVERY_IMAGE;
  public etaComputationControlValue                      = InheritableOnOffFeature.ON;
  public etaComputationControlOverrideAbility            = RegionFeatureOverrideAbility.OVERRIDABLE;
  public cmaCicoAlignment: RegionFeatureStatus           = RegionFeatureStatus.AVAILABLE;
  public displayAutomaticEtaControlValue                 = InheritableOnOffFeature.OFF;
  public displayAutomaticEtaControlOverrideAbility       = RegionFeatureOverrideAbility.OVERRIDABLE;
  public mrDropQuantityUpdateControlValue                = InheritableOnOffFeature.OFF;
  public mrDropQuantityUpdateControlOverrideAbility      = RegionFeatureOverrideAbility.OVERRIDABLE;
  public retrieveCarrierEtaFromByValue                   = InheritableOnOffFeature.OFF;
  public retrieveCarrierEtaFromByOverrideAbility         = RegionFeatureOverrideAbility.OVERRIDABLE;
  public askZ2EtaCico: RegionFeatureStatus               = RegionFeatureStatus.AVAILABLE;
  public z1CheckoutToMyChep: RegionFeatureStatus         = RegionFeatureStatus.AVAILABLE;
  public communicatePickEtaForMrValue                    = InheritableOnOffFeature.OFF;
  public communicatePickEtaForMrOverrideAbility          = RegionFeatureOverrideAbility.OVERRIDABLE;
  public pcMilerUseHosReportValue                        = OnOffFeature.OFF;
  public customizeBluechatFooterValue                    = OnOffFeature.OFF;
  public bluechatNoteFooterFields: BluechatNoteFooterField[] = [];
  public documentDownloadQrCodeAvailability: RegionFeatureStatus = RegionFeatureStatus.AVAILABLE;

  static fromDto(f: any): RegionFeatures {
    if (!f) {
      return null;
    }

    const features = new RegionFeatures();

    features.id                                              = f.id;
    features.loadTracking                                    = RegionFeatureStatus.fromName(f.load_tracking);
    features.hotspotTracking                                 = RegionFeatureStatus.fromName(f.hotspot_tracking);
    features.allowLoadsSearchFiltering                       = RegionFeatureStatus.fromName(f.allow_loads_search_filtering);
    features.trailerYardManagement                           = RegionFeatureStatus.fromName(f.trailer_yard_management);
    features.multiCollections                                = RegionFeatureStatus.fromName(f.multi_collections);
    features.multiDeliveries                                 = RegionFeatureStatus.fromName(f.multi_deliveries);
    features.tipAndReload                                    = RegionFeatureStatus.fromName(f.tip_and_reload);
    features.tmsLoadsSynchronization                         = RegionFeatureStatus.fromName(f.tms_loads_synchronization);
    features.cabinLicensePlateRequired                       = RegionFeatureStatus.fromName(f.cabin_license_plate_required);
    features.sapSignaturesSending                            = RegionFeatureStatus.fromName(f.sap_signatures_sending);

    features.assetManagement                                 = RegionFeatureStatus.fromName(f.asset_management);
    features.quantitiesUpdateTimer                           = RegionFeatureStatus.fromName(f.quantities_update_timer);
    features.driverAssignmentModeOverrideAbility             = f.driver_assignment_mode_override_ability;
    features.driverAssignmentModeValue                       = f.driver_assignment_mode_value;
    features.proofOfDeliveryRuleValue                        = f.pod_rule_value;
    features.proofOfDeliveryRuleOverrideAbility              = f.pod_rule_override_ability
    features.photoPodRuleValue                               = f.photo_pod_rule_value;
    features.photoPodRuleOverrideAbility                     = f.photo_pod_rule_override_ability
    features.dropAndHookValue                                = f.drop_and_hook_value;
    features.dropAndHookOverrideAbility                      = f.drop_and_hook_override_ability
    features.automaticTripCreationValue                      = f.automatic_trip_creation_value;
    features.automaticTripCreationOverrideAbility            = f.automatic_trip_creation_override_ability;
    features.automaticTripCreationOrderTypes                 = f.automatic_trip_creation_order_types;
    features.sapMultiCollectionsCheckinValue                 = f.sap_multi_collections_checkin_value;
    features.sapMultiCollectionsOverrideAbility              = f.sap_multi_collections_override_ability;
    features.autoGroupCollectionsValue                       = f.auto_group_collections_value;
    features.autoGroupCollectionsOverrideAbility             = f.auto_group_collections_override_ability;
    features.multiAccountsValue                              = f.multi_accounts_value;
    features.multiAccountsOverrideAbility                    = f.multi_accounts_override_ability;
    features.overdueNotificationValue                        = f.overdue_notification_value;
    features.overdueNotificationOverrideAbility              = f.overdue_notification_override_ability;
    features.handoverManagementValue                         = f.handover_management_value;
    features.handoverManagementOverrideAbility               = f.handover_management_override_ability;
    features.hideVehicleIdOnDriverInformationValue           = f.hide_vehicle_id_on_driver_information_value;
    features.hideVehicleIdOnDriverInformationOverrideAbility = f.hide_vehicle_id_on_driver_information_override_ability;
    features.overdueMaxDaysUpdate                            = f.overdue_max_days_update;
    features.podValidationManufacturerSyncValue              = f.pod_validation_manufacturer_sync_value;
    features.podValidationManufacturerSyncOverrideAbility    = f.pod_validation_manufacturer_sync_override_ability;
    features.podValidationMrSyncValue                        = f.pod_validation_mr_sync_value;
    features.podValidationMrSyncOverrideAbility              = f.pod_validation_mr_sync_override_ability;
    features.yardCheckTrailersManagement                     = f.yard_check_trailers_management;
    features.yardCheckPalletsManagement                      = f.yard_check_pallets_management;
    features.reverseCheckinNonYMS                            = f.reverse_checkin_non_yms;
    features.reverseCheckinCustomer                          = f.reverse_checkin_customer;
    features.reverseCheckinContactEmails                     = f.reverse_checkin_contact_emails;
    features.reverseCheckinReasons                           = f.reverse_checkin_reasons;
    features.sortLoadsByTripIdValue                          = f.sort_loads_by_trip_id_value;
    features.sortLoadsByTripIdOverrideAbility                = f.sort_loads_by_trip_id_override_ability;
    features.sortStopsByBlueyonderOrderValue                 = f.sort_stops_by_blueyonder_order_value;
    features.sortStopsByBlueyonderOrderOverrideAbility       = f.sort_stops_by_blueyonder_order_override_ability;
    features.sapDocumentTypeUploadToBlueyonder               = f.sap_document_type_upload_to_blueyonder;
    features.tripCreatedWebhookEnrichedDataValue             = f.trip_created_webhook_enriched_data_value;
    features.tripCreatedWebhookEnrichedDataOverrideAbility   = f.trip_created_webhook_enriched_data_override_ability;
    features.etaComputationControlValue                      = f.eta_computation_control_value;
    features.etaComputationControlOverrideAbility            = f.eta_computation_control_override_ability;
    features.cmaCicoAlignment                                = RegionFeatureStatus.fromName(f.cma_cico_alignment);
    features.displayAutomaticEtaControlValue                 = f.display_automatic_eta_control_value;
    features.displayAutomaticEtaControlOverrideAbility       = f.display_automatic_eta_control_override_ability;
    features.mrDropQuantityUpdateControlValue                = f.mr_drop_quantity_update_control_value;
    features.mrDropQuantityUpdateControlOverrideAbility      = f.mr_drop_quantity_update_control_override_ability;
    features.retrieveCarrierEtaFromByValue                   = f.retrieve_carrier_eta_from_by_value;
    features.retrieveCarrierEtaFromByOverrideAbility         = f.retrieve_carrier_eta_from_by_override_ability;
    features.communicatePickEtaForMrValue                    = f.communicate_pick_eta_for_mr_value;
    features.communicatePickEtaForMrOverrideAbility          = f.communicate_pick_eta_for_mr_override_ability;
    features.pcMilerUseHosReportValue                        = f.pc_miler_use_hos_report_value;
    features.customizeBluechatFooterValue                    = f.customize_bluechat_footer_value;
    features.bluechatNoteFooterFields                        = f.bluechat_note_footer_fields;

    features.askZ2EtaCico                                    =  RegionFeatureStatus.fromName(f.ask_z2_eta_cico);
    features.z1CheckoutToMyChep                              =  RegionFeatureStatus.fromName(f.z1_checkout_to_my_chep);
    features.documentDownloadQrCodeAvailability              =  RegionFeatureStatus.fromName(f.document_download_qr_code_availability);

    return features;
  }

  static toDto(f: RegionFeatures): any {
    if (!f) {
      return null;
    }

    return {
      id: f.id,
      load_tracking: f.loadTracking.label,
      hotspot_tracking: f.hotspotTracking.label,
      allow_loads_search_filtering: f.allowLoadsSearchFiltering.label,
      trailer_yard_management: f.trailerYardManagement.label,
      multi_collections: f.multiCollections.label,
      multi_deliveries: f.multiDeliveries.label,
      tip_and_reload: f.tipAndReload.label,
      tms_loads_synchronization: f.tmsLoadsSynchronization.label,
      cabin_license_plate_required: f.cabinLicensePlateRequired.label,
      sap_signatures_sending: f.sapSignaturesSending.label,
      asset_management: f.assetManagement.label,
      quantities_update_timer: f.quantitiesUpdateTimer.label,
      driver_assignment_mode_override_ability: f.driverAssignmentModeOverrideAbility,
      driver_assignment_mode_value: f.driverAssignmentModeValue,
      pod_rule_value: f.proofOfDeliveryRuleValue,
      pod_rule_override_ability: f.proofOfDeliveryRuleOverrideAbility,
      photo_pod_rule_value: f.photoPodRuleValue,
      photo_pod_rule_override_ability: f.photoPodRuleOverrideAbility,
      drop_and_hook_value: f.dropAndHookValue,
      drop_and_hook_override_ability: f.dropAndHookOverrideAbility,
      automatic_trip_creation_value: f.automaticTripCreationValue,
      automatic_trip_creation_override_ability: f.automaticTripCreationOverrideAbility,
      automatic_trip_creation_order_types: f.automaticTripCreationOrderTypes,
      sap_multi_collections_checkin_value: f.sapMultiCollectionsCheckinValue,
      sap_multi_collections_override_ability: f.sapMultiCollectionsOverrideAbility,
      auto_group_collections_value: f.autoGroupCollectionsValue,
      auto_group_collections_override_ability: f.autoGroupCollectionsOverrideAbility,
      multi_accounts_value: f.multiAccountsValue,
      multi_accounts_override_ability: f.multiAccountsOverrideAbility,
      overdue_notification_value: f.overdueNotificationValue,
      overdue_notification_override_ability: f.overdueNotificationOverrideAbility,
      handover_management_value: f.handoverManagementValue,
      handover_management_override_ability: f.handoverManagementOverrideAbility,
      hide_vehicle_id_on_driver_information_value: f.hideVehicleIdOnDriverInformationValue,
      hide_vehicle_id_on_driver_information_override_ability: f.hideVehicleIdOnDriverInformationOverrideAbility,
      overdue_max_days_update: f.overdueMaxDaysUpdate,
      pod_validation_manufacturer_sync_value: f.podValidationManufacturerSyncValue,
      pod_validation_manufacturer_sync_override_ability: f.podValidationManufacturerSyncOverrideAbility,
      pod_validation_mr_sync_value: f.podValidationMrSyncValue,
      pod_validation_mr_sync_override_ability: f.podValidationMrSyncOverrideAbility,
      yard_check_trailers_management: f.yardCheckTrailersManagement,
      yard_check_pallets_management: f.yardCheckPalletsManagement,
      reverse_checkin_non_yms: f.reverseCheckinNonYMS,
      reverse_checkin_customer: f.reverseCheckinCustomer,
      reverse_checkin_contact_emails: f.reverseCheckinContactEmails,
      reverse_checkin_reasons: f.reverseCheckinReasons,
      sort_loads_by_trip_id_value: f.sortLoadsByTripIdValue,
      sort_loads_by_trip_id_override_ability: f.sortLoadsByTripIdOverrideAbility,
      sort_stops_by_blueyonder_order_value: f.sortStopsByBlueyonderOrderValue,
      sort_stops_by_blueyonder_order_override_ability: f.sortStopsByBlueyonderOrderOverrideAbility,
      sap_document_type_upload_to_blueyonder: f.sapDocumentTypeUploadToBlueyonder,
      trip_created_webhook_enriched_data_value: f.tripCreatedWebhookEnrichedDataValue,
      trip_created_webhook_enriched_data_override_ability: f.tripCreatedWebhookEnrichedDataOverrideAbility,
      eta_computation_control_value: f.etaComputationControlValue,
      eta_computation_control_override_ability: f.etaComputationControlOverrideAbility,
      cma_cico_alignment: f.cmaCicoAlignment.label,
      display_automatic_eta_control_value: f.displayAutomaticEtaControlValue,
      display_automatic_eta_control_override_ability: f.displayAutomaticEtaControlOverrideAbility,
      mr_drop_quantity_update_control_value: f.mrDropQuantityUpdateControlValue,
      mr_drop_quantity_update_control_override_ability: f.mrDropQuantityUpdateControlOverrideAbility,
      retrieve_carrier_eta_from_by_value: f.retrieveCarrierEtaFromByValue,
      retrieve_carrier_eta_from_by_override_ability: f.retrieveCarrierEtaFromByOverrideAbility,
      communicate_pick_eta_for_mr_value: f.communicatePickEtaForMrValue,
      communicate_pick_eta_for_mr_override_ability: f.communicatePickEtaForMrOverrideAbility,
      ask_z2_eta_cico: f.askZ2EtaCico.label,
      z1_checkout_to_my_chep: f.z1CheckoutToMyChep.label,
      pc_miler_use_hos_report_value : f.pcMilerUseHosReportValue,
      customize_bluechat_footer_value : f.customizeBluechatFooterValue,
      bluechat_note_footer_fields : f.bluechatNoteFooterFields,
      document_download_qr_code_availability : f.documentDownloadQrCodeAvailability.label
    };
  }
}

enum RegionFeatureOverrideable {
  DRIVER_ASSIGNMENT_MODE                     = 'DRIVER_ASSIGNMENT_MODE',
  PROOF_OF_DELIVERY_RULE                     = 'PROOF_OF_DELIVERY_RULE',
  PHOTO_POD_RULE                             = 'PHOTO_POD_RULE',
  DROP_AND_HOOK                              = 'DROP_AND_HOOK',
  AUTOMATIC_TRIP_CREATION                    = 'AUTOMATIC_TRIP_CREATION',
  SAP_MULTI_COLLECTIONS_CHECKIN              = 'SAP_MULTI_COLLECTIONS_CHECKIN',
  AUTO_GROUP_COLLECTIONS                     = 'AUTO_GROUP_COLLECTIONS',
  MULTI_ACCOUNTS                             = 'MULTI_ACCOUNTS',
  OVERDUE_NOTIFICATION                       = 'OVERDUE_NOTIFICATION',
  HANDOVER_MANAGEMENT                        = 'HANDOVER_MANAGEMENT',
  HIDE_VEHICLE_ID_ON_DRIVER_INFORMATION      = 'HIDE_VEHICLE_ID_ON_DRIVER_INFORMATION',
  POD_VALIDATION_MANUFACTURER_SYNC           = 'POD_VALIDATION_MANUFACTURER_SYNC',
  POD_VALIDATION_MR_SYNC                     = 'POD_VALIDATION_MR_SYNC',
  SORT_LOADS_BY_TRIP_ID                      = 'SORT_LOADS_BY_TRIP_ID',
  SORT_STOPS_BY_BLUEYONDER_ORDER             = 'SORT_STOPS_BY_BLUEYONDER_ORDER',
  TRIP_CREATED_WEBHOOK_ENRICHED_DATA_FEATURE = 'TRIP_CREATED_WEBHOOK_ENRICHED_DATA_FEATURE',
  SAP_DOCUMENT_TYPE_UPLOAD_TO_BLUEYONDER     = 'SAP_DOCUMENT_TYPE_UPLOAD_TO_BLUEYONDER',
  ETA_COMPUTATION_CONTROL                    = 'ETA_COMPUTATION_CONTROL',
  DISPLAY_AUTOMATIC_ETA_CONTROL              = 'DISPLAY_AUTOMATIC_ETA_CONTROL',
  MR_QUANTITY_UPDATE                         = 'MR_QUANTITY_UPDATE',
  RETRIEVE_CARRIER_ETA_FROM_BY               = 'RETRIEVE_CARRIER_ETA_FROM_BY',
  COMMUNICATE_PICK_ETA_FOR_MR                = 'COMMUNICATE_PICK_ETA_FOR_MR',
}

export { RegionFeatures, RegionFeatureOverrideable };
